import React from "react";
import PropTypes from "prop-types";
// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Input from "@material-ui/core/Input";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import { AuthConsumer } from "react-check-auth";
import { BASE_URL, isLocal } from "lib/config";
import httpr from "lib/httpRequest";
import ReactLoading from "react-loading";

const styles = (theme) => ({
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
  formControl: {
    margin: theme.spacing.unit,
    width: "99%",
  },
});

class LicenseManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      locations: [],
      selectedLocationId: null, // Selected location ID for filtering
      loadingLocations: {}, // Track loading state per location
      generatedTokens: {}, // Store generated tokens by locationId
      baseUrls: {}, // Store base URLs by locationId
      editingBaseUrl: {}, // Track which base URLs are being edited
      isAdmin: false,
      isService: false,
    };
  }

  componentDidMount() {
    window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;
    
    // Only show this page for local installations
    if (isLocal !== "true" && isLocal !== true) {
      return;
    }

    // Auto-detect base URL from current window location
    const detectedBaseUrl = window.location.origin;
    this.setState({
      baseUrls: {
        // Will be populated per location when locations are loaded
        _default: detectedBaseUrl
      }
    });
  }



  getMemberPrivileges = (memberId) => {
    const token = localStorage.getItem("token");
    const fetch = httpr(token);

    if (!memberId || !token) {
      return;
    }

    fetch
      .get(`${BASE_URL}/api/members/privileges/${memberId}`)
      .then((response) => {
        const isAdmin = response.data.Admin === 1 ? true : false;
        const isService = response.data.Service === 1 ? true : false;

        if (isAdmin || isService) {
          this.setState({
            isAdmin: isAdmin,
            isService: isService,
          });
        }
      })
      .catch((error) => {
        console.log("Error getting member privileges:", error);
      });
  };

  handleRequestUpdate = (locationId) => {
    const token = localStorage.getItem("token");
    const fetch = httpr(token);

    // Set loading state for this specific location
    this.setState(prevState => ({
      loadingLocations: {
        ...prevState.loadingLocations,
        [locationId]: true
      }
    }));

    fetch
      .post(`${BASE_URL}/api/license/request-update`, {
        locationId: locationId,
      })
      .then((response) => {
        if (response.data && response.data.success) {
          const tokenInfo = response.data;
          const detectedBaseUrl = this.state.baseUrls._default || window.location.origin;
          
          // Store token info in state for this location and set base URL if not already set
          this.setState(prevState => ({
            generatedTokens: {
              ...prevState.generatedTokens,
              [locationId]: tokenInfo
            },
            baseUrls: {
              ...prevState.baseUrls,
              [locationId]: prevState.baseUrls[locationId] || detectedBaseUrl
            },
            loadingLocations: {
              ...prevState.loadingLocations,
              [locationId]: false
            }
          }));
          
          // Optionally copy token to clipboard
          if (navigator.clipboard) {
            navigator.clipboard.writeText(tokenInfo.token);
          }
        } else {
          this.setState(prevState => ({
            loadingLocations: {
              ...prevState.loadingLocations,
              [locationId]: false
            }
          }));
        }
      })
      .catch((error) => {
        console.log("Error requesting license update:", error);
        alert("Failed to generate license update token. Please try again.");
        this.setState(prevState => ({
          loadingLocations: {
            ...prevState.loadingLocations,
            [locationId]: false
          }
        }));
      });
  };

  getLicenseStatus = (license) => {
    if (!license) {
      return { text: "No License", color: "warning" };
    }
    
    if (license.isValid === 0) {
      return { text: "Expired", color: "danger" };
    }
    
    if (license.daysUntilExpiration !== null && license.daysUntilExpiration <= 30) {
      return { 
        text: `Expiring in ${license.daysUntilExpiration} days`, 
        color: "warning" 
      };
    }
    
    if (license.expirationDate) {
      const expirationDate = new Date(license.expirationDate);
      return { 
        text: `Valid until ${expirationDate.toLocaleDateString()}`, 
        color: "success" 
      };
    }
    
    return { text: "Valid", color: "success" };
  };

  handleLocationChange = (event) => {
    this.setState({ selectedLocationId: event.target.value });
  };

  handleBaseUrlChange = (locationId, event) => {
    const baseUrl = event.target.value.trim();
    this.setState(prevState => ({
      baseUrls: {
        ...prevState.baseUrls,
        [locationId]: baseUrl
      }
    }));
  };

  handleEditBaseUrl = (locationId) => {
    this.setState(prevState => ({
      editingBaseUrl: {
        ...prevState.editingBaseUrl,
        [locationId]: true
      }
    }));
  };

  handleSaveBaseUrl = (locationId) => {
    this.setState(prevState => ({
      editingBaseUrl: {
        ...prevState.editingBaseUrl,
        [locationId]: false
      }
    }));
  };

  renderSelectedLocationValue = (locationId) => {
    if (!locationId) return "";
    // This will be called with the locationId, find the location name
    const location = this.state.locations.find(loc => loc.locationId === locationId);
    return location ? location.locationName : "";
  };

  render() {
    const { classes } = this.props;

    // Only show for local installations
    if (isLocal !== "true" && isLocal !== true) {
      return (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="success">
                <h4 className={classes.cardTitleWhite}>License Management</h4>
                <p className={classes.cardCategoryWhite}>
                  This feature is only available for on-premise installations.
                </p>
              </CardHeader>
              <CardBody>
                <p>License management is not available for cloud installations.</p>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      );
    }

    return (
      <AuthConsumer>
        {({ userInfo }) => {
          if (!userInfo || !userInfo.member) {
            return (
              <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                  <Card>
                    <CardBody>
                      <p>Loading...</p>
                    </CardBody>
                  </Card>
                </GridItem>
              </GridContainer>
            );
          }

          const member = userInfo.member;
          const memberId = userInfo.MemberId;
          const locationLicenses = member.LocationLicenses || [];

          // Initialize base URLs for all locations if not already set
          const detectedBaseUrl = this.state.baseUrls._default || window.location.origin;
          const baseUrlsToUpdate = {};
          let needsUpdate = false;
          locationLicenses.forEach(loc => {
            if (!this.state.baseUrls[loc.locationId]) {
              baseUrlsToUpdate[loc.locationId] = detectedBaseUrl;
              needsUpdate = true;
            }
          });
          if (needsUpdate) {
            this.setState(prevState => ({
              baseUrls: {
                ...prevState.baseUrls,
                ...baseUrlsToUpdate
              }
            }));
          }

          // Get admin status - fetch if not already loaded
          if (memberId && !this.state.isAdmin && !this.state.isService) {
            this.getMemberPrivileges(memberId);
          }

          const isAdmin = this.state.isAdmin || this.state.isService;

          // Compute default selected location: use state if set, otherwise default to first location
          const defaultSelectedLocationId = locationLicenses.length > 0 
            ? locationLicenses[0].locationId 
            : null;
          
          const selectedLocationId = this.state.selectedLocationId !== null 
            ? this.state.selectedLocationId 
            : defaultSelectedLocationId;

          // Filter locations based on selection - always show only the selected location
          const displayedLocations = selectedLocationId
            ? locationLicenses.filter(loc => loc.locationId === selectedLocationId)
            : [];

          // Create location menu items
          const locationMenuItems = locationLicenses.map((loc) => (
            <MenuItem key={loc.locationId} value={loc.locationId}>
              {loc.locationName || "Unknown"}
            </MenuItem>
          ));

          return (
            <GridContainer>
              <GridItem xs={12} sm={12} md={12}>
                <Card>
                  <CardHeader color="success">
                    <h4 className={classes.cardTitleWhite}>License Management</h4>
                    <p className={classes.cardCategoryWhite}>
                      Request license update tokens for your locations
                    </p>
                  </CardHeader>
                  <CardBody>
                    {locationLicenses.length === 0 ? (
                      <p>No locations found. Please contact your administrator.</p>
                    ) : (
                      <div>
                        {locationLicenses.length > 1 && (
                          <GridItem xs={12} sm={12} md={12} style={{ marginBottom: "20px" }}>
                            <FormControl className={classes.formControl}>
                              <InputLabel htmlFor="select-location">
                                Select Location
                              </InputLabel>
                              <Select
                                value={selectedLocationId || ""}
                                onChange={this.handleLocationChange}
                                input={<Input id="select-location" />}
                                renderValue={(value) => {
                                  const location = locationLicenses.find(loc => loc.locationId === value);
                                  return location ? location.locationName : "";
                                }}
                              >
                                {locationMenuItems}
                              </Select>
                            </FormControl>
                          </GridItem>
                        )}
                        {displayedLocations.map((loc) => {
                          const license = loc.license || {};
                          const status = this.getLicenseStatus(license);
                          const expirationDate = license.expirationDate 
                            ? new Date(license.expirationDate).toLocaleDateString()
                            : "N/A";
                          const tokenInfo = this.state.generatedTokens[loc.locationId];
                          const isLoading = this.state.loadingLocations[loc.locationId];

                          return (
                            <div key={loc.locationId} style={{ marginBottom: "20px", padding: "15px", border: "1px solid #ddd", borderRadius: "4px" }}>
                              <div style={{ marginBottom: "10px" }}>
                                <strong>{loc.locationName || "Unknown"}</strong>
                              </div>
                              <div style={{ marginBottom: "5px" }}>
                                Status: <span style={{ color: status.color === "danger" ? "#f44336" : status.color === "warning" ? "#ff9800" : "#4caf50" }}>
                                  {status.text}
                                </span>
                              </div>
                              <div style={{ marginBottom: "10px" }}>
                                Expiration: {expirationDate}
                              </div>
                              
                              {tokenInfo ? (
                                <div style={{ marginTop: "15px", padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "4px", border: "1px solid #ccc" }}>
                                  <div style={{ marginBottom: "10px", fontSize: "12px", color: "#666" }}>
                                    <strong>Token expires:</strong> {new Date(tokenInfo.expiresAt).toLocaleString()}
                                  </div>
                                  <div style={{ padding: "10px", backgroundColor: "#e3f2fd", borderRadius: "4px", border: "1px solid #90caf9" }}>
                                    <div style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px", color: "#1976d2" }}>
                                      Next Steps:
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#333", lineHeight: "1.6" }}>
                                      Please email: <strong>brittanydeventer@socalsafe.com, csantillanes@socalsafe.com</strong>
                                      <br />
                                      <br />
                                      Using the subject: <strong>License Update Request for {loc.locationName}</strong>
                                      <br />
                                      <br />
                                      And paste the following into the body of the email:
                                      <div style={{ 
                                        marginTop: "10px", 
                                        padding: "10px", 
                                        backgroundColor: "#f9f9f9", 
                                        border: "1px solid #ddd", 
                                        borderRadius: "4px",
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        whiteSpace: "pre-wrap"
                                      }}>
                                        {(() => {
                                          const baseUrl = this.state.baseUrls[loc.locationId] || this.state.baseUrls._default || window.location.origin;
                                          return `Location Name: ${loc.locationName}
License Update Token: ${tokenInfo.token}${baseUrl ? `\nBase URL: ${baseUrl}` : ''}`;
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              
                              {isAdmin ? (
                                <Button
                                  color={status.color}
                                  size="sm"
                                  onClick={() => this.handleRequestUpdate(loc.locationId)}
                                  disabled={isLoading}
                                  style={{ marginTop: tokenInfo ? "10px" : "0" }}
                                >
                                  {isLoading ? (
                                    <ReactLoading
                                      type="spin"
                                      color="#FFFFFF"
                                      height={20}
                                      width={20}
                                    />
                                  ) : (
                                    tokenInfo ? "Generate New Token" : "Request Update"
                                  )}
                                </Button>
                              ) : (
                                <span>Contact Admin</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardBody>
                  <CardFooter>
                    <p>
                      <strong>Instructions:</strong> Click "Request Update" to generate a token. Copy the information from the "Next Steps" section and email <strong>brittanydeventer@socalsafe.com</strong> with your <strong>location name</strong>, <strong>token</strong>, and <strong>base URL</strong>. 
                      You will receive a new license key and activation URL. Tokens are valid for 14 days.
                    </p>
                  </CardFooter>
                </Card>
              </GridItem>
            </GridContainer>
          );
        }}
      </AuthConsumer>
    );
  }
}

LicenseManagement.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(LicenseManagement);




// WEBPACK FOOTER //
// ./src/views/LicenseManagement/LicenseManagement.jsx